import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { companyPath } from '../utils/company';
import { jobApplicationApi } from '../services/jobApplicationApi';
import { useApplication } from '../hooks/useApplication';
import '../styles/ApplicationModal.css';

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface JobSpecificQuestion {
  id: string;
  label: string;
  type: 'text' | 'select' | 'radio';
  required: boolean;
  options?: string[];
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({ 
  isOpen, 
  onClose
}) => {
  const { applicationData } = useApplication();
  
  // Extract job data from context (will be null for general applications)
  const jobTitle = applicationData?.jobTitle;
  const jobId = applicationData?.jobId;
  const jobSlug = applicationData?.jobSlug;
  const [applicationMethod, setApplicationMethod] = useState<'form' | 'linkedin' | null>('linkedin'); // Default to LinkedIn
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    linkedinUrl: '',
    germanLevel: '',
    yearsOfExperience: '',
    workPermit: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!isOpen || !dialog) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [isOpen, showConfirmation]);

  // Example job-specific questions (these would come from backend based on job/family/domain)
  const jobSpecificQuestions: JobSpecificQuestion[] = [
    {
      id: 'germanLevel',
      label: 'Deutschkenntnisse',
      type: 'select',
      required: false,
      options: ['Keine', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Muttersprache']
    }
  ];

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      addFiles(files);
    }
  };

  const addFiles = (newFiles: File[]) => {
    const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    if (newFiles.some(file => !/\.(pdf|docx?|txt|rtf|png|jpe?g)$/i.test(file.name))) {
      setErrors(prev => ({ ...prev, files: 'Bitte verwende PDF, DOC, DOCX, TXT, RTF, PNG oder JPG.' }));
      return;
    }
    
    // Calculate new total size
    const newFilesSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    const newTotalSize = totalFileSize + newFilesSize;
    
    if (newTotalSize > MAX_TOTAL_SIZE) {
      setErrors(prev => ({
        ...prev,
        files: `Die Gesamtgröße der Dateien darf 25MB nicht überschreiten. Aktuelle Größe: ${(newTotalSize / 1024 / 1024).toFixed(2)}MB`
      }));
      return;
    }
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    setTotalFileSize(newTotalSize);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.files;
      return newErrors;
    });
  };

  const removeFile = (index: number) => {
    const fileToRemove = uploadedFiles[index];
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setTotalFileSize(prev => prev - fileToRemove.size);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.files;
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Email is required for both methods
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (applicationMethod === 'form') {
      if (!formData.firstName.trim()) newErrors.firstName = 'Vorname ist erforderlich';
      if (!formData.lastName.trim()) newErrors.lastName = 'Nachname ist erforderlich';
    } else if (applicationMethod === 'linkedin') {
      if (!formData.linkedinUrl.trim()) {
        newErrors.linkedinUrl = 'LinkedIn-Profil-URL ist erforderlich';
      } else if (!/https?:\/\/([\w]+\.)?linkedin\.com\/.*/.test(formData.linkedinUrl)) {
        newErrors.linkedinUrl = 'Ungültige LinkedIn-URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.general;
      return newErrors;
    });

    // Minimum loading time to ensure visibility
    const minimumLoadingTime = 1500; // 1.5 seconds
    const startTime = Date.now();

    try {
      // Submit application via API
      const response = await jobApplicationApi.submitApplication({
        placementId: applicationData?.placementId,
        placementSnapshotId: applicationData?.placementSnapshotId,
        publicationOperationId: applicationData?.publicationOperationId,
        jobId,
        jobSlug,
        jobTitle,
        applicationMethod: applicationMethod || 'form',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        linkedinUrl: formData.linkedinUrl,
        germanLevel: formData.germanLevel,
        yearsOfExperience: formData.yearsOfExperience,
        workPermit: formData.workPermit,
        documents: uploadedFiles
      });

      console.log('Application submitted successfully:', response);
      
      // Ensure minimum loading time for better UX
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minimumLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsedTime));
      }
      
      // Show confirmation
      setShowConfirmation(true);
    } catch (error) {
      console.error('Failed to submit application:', error);
      
      // Ensure minimum loading time even on error
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minimumLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsedTime));
      }
      
      // Show error to user
      setErrors(prev => ({
        ...prev,
        general: 'Die Bewerbung konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    if (isSubmitting) return;
    setApplicationMethod('linkedin');
    setShowConfirmation(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      linkedinUrl: '',
      germanLevel: '',
      yearsOfExperience: '',
      workPermit: ''
    });
    setUploadedFiles([]);
    setIsDragging(false);
    setTotalFileSize(0);
    setErrors({});
    setIsSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (showConfirmation) {
    return (
      <dialog ref={dialogRef} className="application-dialog" aria-labelledby="application-confirmation-title" onCancel={event => { event.preventDefault(); resetAndClose(); }}>
        <div className="modal-content confirmation-content">
          <div className="confirmation-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h2 id="application-confirmation-title" className="confirmation-title">Bewerbung erfolgreich eingereicht!</h2>
          <p className="confirmation-message">
            Vielen Dank für Ihre Bewerbung{jobTitle ? ` als ${jobTitle}` : ''}. 
            Wir haben Ihre Unterlagen erhalten und werden uns schnellstmöglich bei Ihnen melden.
          </p>
          <button className="btn-primary" onClick={resetAndClose}>
            Schließen
          </button>
        </div>
      </dialog>
    );
  }

  return (
    <dialog ref={dialogRef} className="application-dialog" aria-labelledby="application-title" aria-describedby={jobTitle ? 'application-job' : undefined} onCancel={event => { event.preventDefault(); resetAndClose(); }}>
      <div className="modal-content">
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p className="loading-text">Ihre Bewerbung wird gesendet...</p>
          </div>
        )}
        <button className="modal-close" aria-label="Bewerbung schließen" onClick={resetAndClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <h2 id="application-title" className="modal-title">Deine Bewerbung</h2>
        {jobTitle && <p id="application-job" className="application-job">{jobTitle}</p>}

        {/* Tab Selection for Application Method */}
        <div className="application-tabs" role="group" aria-label="Bewerbungsweg">
          <button 
            className={`tab-button ${applicationMethod === 'form' ? 'active' : ''}`}
            aria-pressed={applicationMethod === 'form'}
            onClick={() => setApplicationMethod('form')}
          >
            <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            Formular-Bewerbung
          </button>
          <button 
            className={`tab-button ${applicationMethod === 'linkedin' ? 'active' : ''}`}
            aria-pressed={applicationMethod === 'linkedin'}
            onClick={() => setApplicationMethod('linkedin')}
          >
            <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
            LinkedIn-Bewerbung
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="application-form">
          {applicationMethod === 'form' && (
            <>
              {/* Basic Information */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Max"
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nachname *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Mustermann"
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">E-Mail Adresse *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="max.mustermann@beispiel.de"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Optional LinkedIn */}
              <div className="form-group">
                <label htmlFor="linkedinUrl">
                  LinkedIn-Profil
                  <span className="optional-badge">Optional - hilft uns, Sie besser kennenzulernen</span>
                </label>
                <input
                  type="url"
                  id="linkedinUrl"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  placeholder="https://www.linkedin.com/in/ihr-profil"
                />
              </div>

              {/* Job-specific questions */}
              {jobSpecificQuestions.map(question => (
                <div key={question.id} className="form-group">
                  <label htmlFor={question.id}>
                    {question.label}
                    {!question.required && <span className="optional-badge">Optional</span>}
                  </label>
                  {question.type === 'select' && (
                    <select
                      id={question.id}
                      name={question.id}
                      value={formData[question.id as keyof typeof formData]}
                      onChange={handleInputChange}
                      required={question.required}
                    >
                      <option value="">Bitte wählen</option>
                      {question.options?.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </>
          )}

          {applicationMethod === 'linkedin' && (
            <>
              <div className="form-group">
                <label htmlFor="email">E-Mail Adresse *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="max.mustermann@beispiel.de"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
                <p className="field-hint">
                  Wir benötigen Ihre E-Mail-Adresse, um Sie über den Status Ihrer Bewerbung zu informieren.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="linkedinUrl">LinkedIn Profil-URL *</label>
                <input
                  type="url"
                  id="linkedinUrl"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  placeholder="https://www.linkedin.com/in/ihr-profil"
                  className={errors.linkedinUrl ? 'error' : ''}
                />
                {errors.linkedinUrl && <span className="error-message">{errors.linkedinUrl}</span>}
                <p className="field-hint">
                  Wir werden Ihr LinkedIn-Profil verwenden, um Ihre Bewerbung zu bearbeiten.
                </p>
              </div>
            </>
          )}

          {/* Document Upload - Only available for form method */}
          {applicationMethod === 'form' && (
            <div className="form-group">
              <label>
                Dokumente hochladen
                <span className="optional-badge">Lebenslauf, Zeugnisse, etc. (max. 25MB gesamt)</span>
              </label>
              <div 
                className={`drop-zone ${isDragging ? 'dragging' : ''} ${errors.files ? 'error' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Dokumente auswählen"
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); fileInputRef.current?.click(); } }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg"
                  style={{ display: 'none' }}
                />
                <svg className="drop-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <p className="drop-text">
                  Dateien hier ablegen oder klicken zum Auswählen
                </p>
                <p className="drop-hint">
                  PDF, DOC, DOCX, Bilder (Gesamt: {(totalFileSize / 1024 / 1024).toFixed(2)} / 25 MB)
                </p>
              </div>
              {errors.files && <span className="error-message">{errors.files}</span>}

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="uploaded-file">
                      <span className="file-name">
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        className="remove-file"
                        aria-label={`${file.name} entfernen`}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* General Error Display */}
          {errors.general && (
            <div className="form-group">
              <div className="error-message" style={{ 
                color: '#ef4444', 
                fontSize: '0.9rem', 
                padding: '0.75rem', 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                {errors.general}
              </div>
            </div>
          )}

          <p className="application-privacy">Informationen zur Verarbeitung deiner Bewerbung findest du im <Link to={companyPath('datenschutz-bewerber')} target="_blank" rel="noopener">Bewerberdatenschutz</Link>.</p>
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn-primary btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Wird gesendet...
                </>
              ) : (
                'Jetzt bewerben'
              )}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
};

export default ApplicationModal;
